import {groupBy} from '@benchling/js-utils';
import gql from 'graphql-tag';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {SchemaTag} from 'folder-items/FileConstants';
import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import notifications from 'notifications';
import MethodEditorContext from 'procedures/design/method-config/MethodEditorContext';
import {useNullableProcedurePageContext} from 'procedures/ProcedurePageContext';
import {
  getPowAgnosticLatestFlowchartVersionFromProcedure,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {
  MethodDefinitionVersionTestOrderConfig,
  PlateDataParameter,
  PlateMapProcedureContext,
  PlateMaterialInput,
  PlateProcedureParameterType,
  SUPPORTED_FIELD_TYPES_FOR_MATERIAL_INPUTS,
} from 'samples/plate-maps/PlateMapConstants';
import APIUtil from 'util/APIUtil';

import {
  ParametersForPlateMapInConfigQuery,
  ParametersForPlateMapInConfigQueryVariables,
} from './__generated/ParametersForPlateMapInConfigQuery';
import {
  ParametersForPlateMapInExecutionQuery,
  ParametersForPlateMapInExecutionQueryVariables,
} from './__generated/ParametersForPlateMapInExecutionQuery';

type WrapperChild = ({
  isInProcedure,
  plateDataParameters,
  plateMaterialInputs,
  procedureStudyDesignType,
  procedureId,
  procedureTestOrderConfig,
  procedureMethodExecutionInstanceId,
}: PlateMapProcedureContext & {procedureMethodExecutionInstanceId: string | null}) => JSX.Element;

export const PARAMETERS_FOR_PLATE_MAP_IN_EXECUTION_QUERY = gql`
  query ParametersForPlateMapInExecutionQuery($stepExecutionInstanceId: StrictString!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    procedureStepExecutionInstance(id: $stepExecutionInstanceId) {
      ... on ProcedureStepExecutionInstanceNode {
        id
        methodExecutionInstance {
          id
          parameterUsagesExpensive {
            stepExecutionInstanceId
            parameterVersionId
          }
          parameterConfirmationMethods {
            parameterId
            confirmationMethod
          }
          procedureRun {
            id
            study {
              ... on StudyNode {
                id
                studyDesign {
                  id
                  type
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                procedureFlowchart {
                  id
                  parent {
                    id
                  }
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            procedureFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
                procedure {
                  id
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
          }
          ...ProcedureMethodExecutionInstancePlannedValuesGraphQL
          planMethodDefinitionVersion {
            id
            dataParameterSettings {
              version {
                id
                name
                restValueField
                procedureDataParameterId
              }
            }
            materialInputSettings {
              version {
                id
                name
                restMaterialField
                procedureMaterialInputId
              }
            }
            testDefinitions {
              ... on TestDefinitionNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
            dilutionPlan {
              allowedDilutionFactors
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ProcedureMethodExecutionInstancePlannedValues').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const PROCEDURE_METHOD_DEFINITION_VERSION_FOR_PLATE_MAP_FRAGMENT = gql`
  fragment ProcedureMethodDefinitionVersionForPlateMapGraphQL on ProcedureMethodDefinitionVersionNode {
    id
    dataParameterSettings {
      version {
        id
        name
        restValueField
        confirmationMethod
      }
      values {
        id
        value
      }
    }
    materialInputSettings {
      version {
        id
        name
        restMaterialField
        confirmationMethod
      }
      values {
        id
        material
      }
    }
    testDefinitions {
      ... on TestDefinitionNode {
        id
      }
      ...InaccessibleInfoGraphQL
    }
    dilutionPlan {
      allowedDilutionFactors
    }
    parameterUsagesExpensive {
      stepId
      parameterVersionId
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// This query is used during config, so it needs to use the /latest/ flowchart version (not /published/)
export const PARAMETERS_FOR_PLATE_MAP_IN_CONFIG_QUERY = gql`
  query ParametersForPlateMapInConfigQuery($procedureId: StrictString!)
  @skipAudit
  @engComponent(name: PROCEDURES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        id
        procedureFlowchart {
          id
          latestFlowchartVersion {
            ... on ProcedureFlowchartVersionNode {
              id
              nodes {
                ...ProcedureMethodDefinitionVersionForPlateMapGraphQL
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
        latestProcedureFlowchartConfigVersionProxy {
          id
          nodes {
            ...ProcedureMethodDefinitionVersionForPlateMapGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${PROCEDURE_METHOD_DEFINITION_VERSION_FOR_PLATE_MAP_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * This subcomponent handles the procedure execution worksheet scenario.
 */
function ParametersForPlateMapInExecutionWrapper(props: {
  deprecatedStepCreationContextId: string;
  children: WrapperChild;
}): JSX.Element | null {
  const queryResult = useBenchlingQuery<
    ParametersForPlateMapInExecutionQuery,
    ParametersForPlateMapInExecutionQueryVariables
  >(PARAMETERS_FOR_PLATE_MAP_IN_EXECUTION_QUERY, {
    variables: {stepExecutionInstanceId: props.deprecatedStepCreationContextId},
  });

  // Guarantee referential integrity when nothing changes. Some children use them as memo/effect dependencies.
  const {
    plateUsedDataParamVersions,
    plateUsedMaterialInputs,
  }: {
    plateUsedDataParamVersions: Array<PlateDataParameter>;
    plateUsedMaterialInputs: Array<PlateMaterialInput>;
  } = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return {plateUsedDataParamVersions: [], plateUsedMaterialInputs: []};
    }
    assert(
      !isInaccessible(queryResult.data.procedureStepExecutionInstance),
      'Expecting step execution instance to be accessible'
    );
    const currentStepExecutionInstanceId = queryResult.data.procedureStepExecutionInstance.id;
    const pmei = queryResult.data.procedureStepExecutionInstance.methodExecutionInstance;
    const planVersion = pmei.planMethodDefinitionVersion;
    const inCurrentStepMap = pmei.parameterUsagesExpensive.reduce(
      (acc: {[parameterVersionId: string]: boolean}, {stepExecutionInstanceId, parameterVersionId}) => {
        // A parameter is in step if that parameter is assigned to the current step (regardless of how many
        //  other steps it is assigned to).
        acc[parameterVersionId] ??= false;
        acc[parameterVersionId] ||= stepExecutionInstanceId === currentStepExecutionInstanceId;
        return acc;
      },
      {}
    );
    const confirmationMethodByParameterIdMap = new Map<string, ProcedureParameterConfirmationMethod>(
      pmei.parameterConfirmationMethods.map(({parameterId, confirmationMethod}) => [
        parameterId,
        confirmationMethod,
      ])
    );

    const plateUsedGQLMaterialInputs = planVersion.materialInputSettings.filter(
      ({version}) =>
        confirmationMethodByParameterIdMap.get(version.procedureMaterialInputId) ===
        ProcedureParameterConfirmationMethod.PLATE_MAP
    );

    assert(
      plateUsedGQLMaterialInputs.every(
        ({
          version: {
            restMaterialField: {fieldType},
          },
        }) => SUPPORTED_FIELD_TYPES_FOR_MATERIAL_INPUTS.has(fieldType)
      ),
      'Expected plate map confirmed material inputs to have valid data type'
    );

    const plateMaterialInputValuesByParameterVersionId = groupBy(
      pmei.materialInputPlannedValues,
      (value) => value.parameterVersion.id
    );
    const plateDataParamValuesByParameterVersionId = groupBy(
      pmei.dataParameterPlannedValues,
      (value) => value.parameterVersion.id
    );

    const plateUsedDataParamVersions = planVersion.dataParameterSettings
      .filter(
        ({version}) =>
          confirmationMethodByParameterIdMap.get(version.procedureDataParameterId) ===
          ProcedureParameterConfirmationMethod.PLATE_MAP
      )
      .map<PlateDataParameter>(({version}) => ({
        type: PlateProcedureParameterType.DATA_PARAMETER,
        versionId: version.id,
        name: version.name,
        schemaField: version.restValueField,
        plannedValues: (plateDataParamValuesByParameterVersionId[version.id] ?? []).map(
          ({value}: {value: Omit<SchemaTag, 'name' | 'tagSchemaField'>}) => ({
            ...value,
            tagSchemaField: version.restValueField,
            name: version.restValueField.name,
          })
        ),
        inStep: inCurrentStepMap[version.id] ?? false,
      }));
    const plateUsedMaterialInputs = plateUsedGQLMaterialInputs.map<PlateMaterialInput>(({version}) => ({
      type: PlateProcedureParameterType.MATERIAL_INPUT,
      versionId: version.id,
      name: version.name,
      schemaField: version.restMaterialField,
      // Planned values may be missing for process intermediates; in this case we still want to include the
      // parameter version, just with no planned values.
      plannedValues: (plateMaterialInputValuesByParameterVersionId[version.id] ?? []).map(
        ({material}: {material: Omit<SchemaTag, 'name' | 'tagSchemaField'>}) => ({
          ...material,
          tagSchemaField: version.restMaterialField,
          name: version.restMaterialField.name,
        })
      ),
      inStep: inCurrentStepMap[version.id] ?? false,
    }));

    return {
      plateUsedDataParamVersions,
      plateUsedMaterialInputs,
    };
  }, [queryResult]);

  if (queryResult.type === QueryResultType.PENDING) {
    return (
      <>
        {props.children({
          isInProcedure: true,
          procedureStudyDesignType: null,
          procedureId: null,
          procedureMethodExecutionInstanceId: null,
        })}
      </>
    );
  }
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load parameter information; try reloading the page.');
    return null;
  }

  assert(
    !isInaccessible(queryResult.data.procedureStepExecutionInstance),
    'Expecting step execution instance to be accessible'
  );
  const pmei = queryResult.data.procedureStepExecutionInstance.methodExecutionInstance;
  const planVersion = pmei.planMethodDefinitionVersion;
  const procedureStudyDesignType =
    pmei.procedureRun.study && !isInaccessible(pmei.procedureRun.study)
      ? (pmei.procedureRun.study.studyDesign?.type ?? null)
      : null;
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(pmei.procedureRun);
  const procedureParent = !isInaccessible(flowchartVersion)
    ? getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion)
    : null;
  const procedureId = procedureParent != null ? procedureParent.id : null;
  const procedureTestOrderConfig: MethodDefinitionVersionTestOrderConfig = {
    testDefinitionIds: planVersion.testDefinitions.map((testDef) => getAccessibleOrInaccessibleId(testDef)),
    allowedDilutionFactors: planVersion.dilutionPlan.allowedDilutionFactors,
  };

  return (
    <>
      {props.children({
        isInProcedure: true,
        plateDataParameters: plateUsedDataParamVersions,
        plateMaterialInputs: plateUsedMaterialInputs,
        procedureStudyDesignType,
        procedureId,
        procedureTestOrderConfig,
        procedureMethodExecutionInstanceId: pmei.id,
      })}
    </>
  );
}

function ParametersForPlateMapInConfigWrapper(props: {
  procedureId: string;
  methodVersionId: string;
  stepVersionId: string;
  children: WrapperChild;
}): JSX.Element | null {
  const {procedureId, methodVersionId, stepVersionId} = props;
  const queryResult = useBenchlingQuery<
    ParametersForPlateMapInConfigQuery,
    ParametersForPlateMapInConfigQueryVariables
  >(PARAMETERS_FOR_PLATE_MAP_IN_CONFIG_QUERY, {
    variables: {procedureId: props.procedureId},
  });

  // Guarantee referential integrity when nothing changes. Some children use them as memo/effect dependencies.
  const {
    plateUsedDataParamVersions,
    plateUsedMaterialInputs,
    procedureTestOrderConfig,
  }: {
    plateUsedDataParamVersions: Array<PlateDataParameter>;
    plateUsedMaterialInputs: Array<PlateMaterialInput>;
    procedureTestOrderConfig: MethodDefinitionVersionTestOrderConfig | null;
  } = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return {plateUsedDataParamVersions: [], plateUsedMaterialInputs: [], procedureTestOrderConfig: null};
    }
    assert(
      !isInaccessible(queryResult.data.procedure),
      'Plate map wrapper should only be used with a known accessible procedure'
    );

    const latestFlowchartVersion = getPowAgnosticLatestFlowchartVersionFromProcedure(
      queryResult.data.procedure
    );
    assert(latestFlowchartVersion != null, 'Latest flowchart version should not be null');
    assert(
      !isInaccessible(latestFlowchartVersion),
      'Plate map wrapper should only be used with a known accessible procedure flowchart version'
    );
    const methodVersion = latestFlowchartVersion?.nodes.find(({id}) => id === methodVersionId);
    if (methodVersion == null) {
      return {plateUsedDataParamVersions: [], plateUsedMaterialInputs: [], procedureTestOrderConfig: null};
    }

    const inCurrentStepMap = methodVersion.parameterUsagesExpensive.reduce(
      (acc: {[parameterVersionId: string]: boolean}, {stepId, parameterVersionId}) => {
        // A parameter is in step if that parameter is assigned to the current step (regardless of how many
        //  other steps it is assigned to).
        acc[parameterVersionId] ??= false;
        acc[parameterVersionId] ||= stepId === stepVersionId;
        return acc;
      },
      {}
    );

    const plateUsedGQLMaterialInputs = methodVersion.materialInputSettings.filter(
      ({version: {confirmationMethod}}) =>
        confirmationMethod === ProcedureParameterConfirmationMethod.PLATE_MAP
    );

    assert(
      plateUsedGQLMaterialInputs.every(
        ({
          version: {
            restMaterialField: {fieldType},
          },
        }) => SUPPORTED_FIELD_TYPES_FOR_MATERIAL_INPUTS.has(fieldType)
      ),
      'Expected plate map confirmed material inputs to have valid data type'
    );

    const plateUsedDataParamVersions = methodVersion.dataParameterSettings
      .filter(
        ({version: {confirmationMethod}}) =>
          confirmationMethod === ProcedureParameterConfirmationMethod.PLATE_MAP
      )
      .map<PlateDataParameter>(({version, values}) => ({
        type: PlateProcedureParameterType.DATA_PARAMETER,
        versionId: version.id,
        name: version.name,
        schemaField: version.restValueField,
        plannedValues: values.map(({value}: {value: Omit<SchemaTag, 'name' | 'tagSchemaField'>}) => ({
          ...value,
          tagSchemaField: version.restValueField,
          name: version.restValueField.name,
        })),
        inStep: inCurrentStepMap[version.id] ?? false,
      }));
    const plateUsedMaterialInputs = plateUsedGQLMaterialInputs.map<PlateMaterialInput>(
      ({version, values}) => ({
        type: PlateProcedureParameterType.MATERIAL_INPUT,
        versionId: version.id,
        name: version.name,
        schemaField: version.restMaterialField,
        plannedValues: values.map(({material}: {material: Omit<SchemaTag, 'name' | 'tagSchemaField'>}) => ({
          ...material,
          tagSchemaField: version.restMaterialField,
          name: version.restMaterialField.name,
        })),
        inStep: inCurrentStepMap[version.id] ?? false,
      })
    );

    const procedureTestOrderConfig: MethodDefinitionVersionTestOrderConfig = {
      testDefinitionIds: methodVersion.testDefinitions.map((testDef) =>
        getAccessibleOrInaccessibleId(testDef)
      ),
      allowedDilutionFactors: methodVersion.dilutionPlan.allowedDilutionFactors,
    };

    return {
      plateUsedDataParamVersions,
      plateUsedMaterialInputs,
      procedureTestOrderConfig,
    };
  }, [methodVersionId, queryResult, stepVersionId]);

  if (queryResult.type === QueryResultType.PENDING) {
    return (
      <>
        {props.children({
          isInProcedure: true,
          procedureStudyDesignType: null,
          procedureId: null,
          procedureMethodExecutionInstanceId: null,
        })}
      </>
    );
  }
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load parameter information; try reloading the page.');
    return null;
  }

  assert(
    !isInaccessible(queryResult.data.procedure),
    'Plate map wrapper should only be used with a known accessible procedure'
  );

  return (
    <>
      {props.children({
        isInProcedure: true,
        procedureId,
        plateDataParameters: plateUsedDataParamVersions,
        plateMaterialInputs: plateUsedMaterialInputs,
        procedureTestOrderConfig,
        // N/A in config
        procedureMethodExecutionInstanceId: null,
        procedureStudyDesignType: null,
      })}
    </>
  );
}

/**
 * This wrapper is used within the context of a worksheet and provides its child with procedure parameter
 * information in the form of PlateDataParameters. It can be safely used outside of a procedure step document,
 * where it gracefully passes down an empty list to represent "no parameters found".
 *
 * This encapsulates all procedures domain-specific logic to minimize mixing procedures concerns into general
 * worksheet / plate map components.
 */
export default function ParametersForPlateMapWrapper(props: {children: WrapperChild}): JSX.Element | null {
  const {deprecatedStepCreationContextId} = WorksheetContext.useContext();
  const procedurePageContext: {
    procedureId: string;
    selectedMethodCid: string | null;
    idToCidMap: Record<string, string>;
  } | null = useNullableProcedurePageContext();
  const {selectedStepCid} = MethodEditorContext.useContext();

  const isInProcedureExecution =
    deprecatedStepCreationContextId != null &&
    APIUtil.isProcedureStepExecutionInstance(deprecatedStepCreationContextId);
  const isInProcedureConfig = procedurePageContext != null;
  assert(
    !isInProcedureExecution || !isInProcedureConfig,
    'Procedure config and execution are mutually exclusive contexts'
  );

  if (isInProcedureExecution) {
    return (
      <ParametersForPlateMapInExecutionWrapper
        deprecatedStepCreationContextId={deprecatedStepCreationContextId}
      >
        {props.children}
      </ParametersForPlateMapInExecutionWrapper>
    );
  }

  if (isInProcedureConfig && procedurePageContext.selectedMethodCid != null && selectedStepCid != null) {
    const {procedureId, selectedMethodCid, idToCidMap} = procedurePageContext;
    const cidToIdMap = Object.fromEntries(Object.entries(idToCidMap).map(([id, cid]) => [cid, id]));
    const methodVersionId = cidToIdMap[selectedMethodCid];
    const stepVersionId = cidToIdMap[selectedStepCid];
    if (methodVersionId != null) {
      return (
        <ParametersForPlateMapInConfigWrapper
          procedureId={procedureId}
          methodVersionId={methodVersionId}
          stepVersionId={stepVersionId}
          key={methodVersionId} // Force re-query of latest parameters when method version changes
        >
          {props.children}
        </ParametersForPlateMapInConfigWrapper>
      );
    }
  }

  return (
    <>
      {props.children({
        isInProcedure: false,
        procedureStudyDesignType: null,
        procedureId: null,
        procedureMethodExecutionInstanceId: null,
      })}
    </>
  );
}
